/**
 * ADR-0177 — starter guardrail template picker.
 *
 * Curated cousin of `PolicyImportModal.tsx`. Fetches the template
 * catalog via `api.policy.listTemplates()` on open, renders each
 * `TemplateDescriptor` as a card, and — on "Load into draft" — hands
 * the descriptor's rules to the caller which drops them into the
 * merchant's draft. Loading is one-time-and-diverges: the template
 * has no back-link and no auto-resync; a new template version does
 * NOT retroactively apply.
 *
 * Layout, theme tokens, and interaction pattern mirror
 * `PolicyImportModal.tsx` deliberately — the coral-dim confirm row
 * that gates a replace-over-merchant-authored publish is the SAME
 * component pattern (typed checkbox, versioned domain label). We
 * REUSE the pattern, not re-invent it. The picker only reaches into
 * that pattern when the merchant's active policy is NOT the
 * ADR-0080 untouched baseline — loading over the baseline is
 * treated as a rename-in-effect (per ADR-0177 §Consequences), not a
 * destructive replace.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AlertOctagon, Download } from 'lucide-react';

import type { Rule, RuleCategory } from '@sill/types';
import type { TemplateDescriptor } from '@sill/types/api';

import { Modal } from '../../components/atoms/index.js';
import { useC } from '../../lib/theme.js';
import { useApi } from '../../api/api-context.js';
import { CATEGORIES } from './catalog.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export interface PolicyTemplatePickerModalProps {
  open: boolean;
  onClose: () => void;
  site_domain: string | undefined;
  /**
   * True when replacing the ACTIVE policy would clobber merchant-
   * authored work — the picker shows the coral-dim confirm row and
   * gates "Load into draft" until the typed confirm is checked.
   *
   * False when the active policy is either absent (no clobber to
   * confirm) or the untouched ADR-0080 baseline (loading it as a
   * template is the byte-identical rename case; not destructive).
   */
  requiresReplaceConfirm: boolean;
  /**
   * Called with the selected template's descriptor when the merchant
   * clicks "Load into draft". The parent replaces the current draft
   * with `descriptor.rules`, marks dirty, and closes the modal.
   */
  onLoadTemplate: (descriptor: TemplateDescriptor) => void;
}

/**
 * Small mono chip used for the per-category rule-breakdown row. Kept
 * local — the modal is the only surface that uses this particular
 * density.
 */
function Chip({
  C,
  label,
}: {
  C: ReturnType<typeof useC>;
  label: string;
}): JSX.Element {
  return (
    <span
      style={{
        fontFamily: monoFont,
        fontSize: 9,
        color: C.muted,
        background: C.surface2,
        border: `1px solid ${C.muted}`,
        padding: '1px 6px',
        borderRadius: 2,
        letterSpacing: 1,
      }}
    >
      {label}
    </span>
  );
}

/**
 * "Show more" should appear ONLY when the 3-line clamp actually cuts
 * something off in the RENDERED card — not based on a character-count
 * guess. A char-count heuristic can't know the real container width,
 * so it either over-triggers on a wide desktop modal (a toggle that
 * only reveals half a line — founder feedback, 2026-07-10: "a little
 * silly") or under-triggers on a narrow mobile one. This measures the
 * real thing: `scrollHeight` on a `-webkit-line-clamp`'d box reports
 * the FULL unclamped content height (the clamp only affects paint,
 * not layout — a well-established browser behavior for this exact
 * technique) while `clientHeight` reports the clamped, visible
 * height; a genuine overflow is `scrollHeight > clientHeight`.
 *
 * Measured in `useLayoutEffect` (fires after the DOM commits, before
 * paint — no visible flash of the wrong toggle state) on mount and on
 * window resize, so a modal that's genuinely narrower (mobile) shows
 * the toggle exactly when text is really cut off, and a wide desktop
 * card with room to spare never shows it at all.
 *
 * jsdom has no real layout engine (`scrollHeight`/`clientHeight` are
 * always 0), so this measurement always resolves to "not overflowing"
 * under test by default — tests that need to exercise the "long text,
 * shows toggle" path stub `scrollHeight`/`clientHeight` directly
 * (`Object.defineProperty` on the element), the standard jsdom
 * technique for this class of layout-dependent behavior.
 *
 * The full string is ALWAYS in the DOM regardless of clamp state (CSS
 * `-webkit-line-clamp` only affects paint), so this never hides text
 * from anything that reads `textContent` (tests, screen readers with
 * clamping disabled, etc.) — only from paint until expanded.
 */
const CLAMP_LINES = 3;

function ExpandableText({
  C,
  text,
  fontSize,
  color,
  fontWeight,
}: {
  C: ReturnType<typeof useC>;
  text: string;
  fontSize: number;
  color: string;
  fontWeight?: number;
}): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    // Only the COLLAPSED (clamped) layout can overflow by definition —
    // once expanded, the clamp styles are removed and scrollHeight ===
    // clientHeight trivially. Re-measure whenever collapsing back (the
    // effect re-runs on `expanded` changing) and whenever the window
    // resizes, so a modal that goes from wide-desktop to narrow-mobile
    // (or vice versa) re-evaluates rather than sticking with a stale
    // answer from first mount.
    if (expanded) return;
    const el = textRef.current;
    if (el === null) return;
    const measure = (): void => {
      setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [expanded, text]);

  return (
    <div>
      <div
        ref={textRef}
        style={{
          fontFamily: sansFont,
          fontSize,
          fontWeight,
          color,
          lineHeight: 1.6,
          ...(!expanded
            ? {
                display: '-webkit-box',
                WebkitLineClamp: CLAMP_LINES,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }
            : {}),
        }}
      >
        {text}
      </div>
      {isOverflowing ? (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1"
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            fontFamily: sansFont,
            fontSize: 11,
            fontWeight: 600,
            color: C.limeFg,
            cursor: 'pointer',
          }}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      ) : null}
    </div>
  );
}

interface CategoryCount {
  id: RuleCategory;
  label: string;
  count: number;
}

/** Derive per-category rule counts from the template's rules array. */
function categoryCountsFor(rules: readonly Rule[]): readonly CategoryCount[] {
  return CATEGORIES.map((cat) => ({
    id: cat.id,
    label: cat.label,
    count: rules.filter((r) => r.category === cat.id).length,
  })).filter((row) => row.count > 0);
}

export function PolicyTemplatePickerModal(
  props: PolicyTemplatePickerModalProps,
): JSX.Element {
  const C = useC();
  const api = useApi();
  const { open, onClose, site_domain, requiresReplaceConfirm, onLoadTemplate } =
    props;

  const [templates, setTemplates] = useState<TemplateDescriptor[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  // Fetch the catalog on open. Reset all interaction state.
  useEffect(() => {
    if (!open) return;
    setConfirmReplace(false);
    setSelectedKey(null);
    setLoadError(null);
    setLoading(true);
    let cancelled = false;
    void (async () => {
      try {
        const list = await api.policy.listTemplates();
        if (cancelled) return;
        setTemplates(list);
      } catch (e) {
        if (cancelled) return;
        setLoadError(
          e instanceof Error
            ? e.message
            : 'Could not load the starter template catalog.',
        );
        setTemplates([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, api]);

  const domainLabel = site_domain ?? 'this site';

  const keyOf = (t: TemplateDescriptor): string =>
    `${t.template_slug}@${t.template_version}`;

  const canLoad = (t: TemplateDescriptor): boolean =>
    !loading && (!requiresReplaceConfirm || confirmReplace);

  const onLoad = (t: TemplateDescriptor): void => {
    if (!canLoad(t)) {
      setSelectedKey(keyOf(t));
      return;
    }
    onLoadTemplate(t);
  };

  const subtitle = useMemo(
    () =>
      'Pick a starting point, then customize freely — the rules become yours to edit right away. If Sill updates this template later, your site won’t change automatically.',
    [],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Load a starter template"
      subtitle={subtitle}
    >
      <div className="p-6 space-y-5">
        {/* Coral-dim confirm row — only when replacing merchant-authored work.
            Mirrors PolicyImportModal.tsx's confirm-row exactly. */}
        {requiresReplaceConfirm ? (
          <div
            className="p-3"
            style={{
              background: C.coralDim,
              border: `1px solid ${C.coralFg}`,
              borderRadius: 2,
            }}
            data-testid="policy-template-picker-confirm-row"
          >
            <label
              className="flex items-start gap-2"
              style={{
                fontFamily: sansFont,
                fontSize: 12,
                color: C.text,
                lineHeight: 1.5,
              }}
            >
              <input
                type="checkbox"
                checked={confirmReplace}
                onChange={(e) => setConfirmReplace(e.target.checked)}
                style={{ marginTop: 3 }}
                data-testid="policy-template-picker-confirm-checkbox"
              />
              <span>
                Loading a template replaces my current draft rules on{' '}
                <strong>{domainLabel}</strong> — this can’t be undone.
              </span>
            </label>
          </div>
        ) : null}

        {/* Load-error banner */}
        {loadError !== null ? (
          <div
            className="p-2 flex items-start gap-2"
            style={{
              background: C.coralDim,
              border: `1px solid ${C.coralFg}`,
              borderRadius: 2,
            }}
            data-testid="policy-template-picker-error"
          >
            <AlertOctagon
              size={12}
              color={C.coralFg}
              className="flex-shrink-0 mt-0.5"
            />
            <div className="flex-1 min-w-0">
              <div style={{ fontFamily: sansFont, fontSize: 12, color: C.text }}>
                {loadError}
              </div>
            </div>
          </div>
        ) : null}

        {/* Loading / empty / list */}
        {loading ? (
          <div
            className="p-6 text-center"
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              letterSpacing: 1.5,
              color: C.muted,
            }}
          >
            LOADING CATALOG…
          </div>
        ) : templates !== null && templates.length === 0 ? (
          <div
            className="p-6 text-center"
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.dim,
              lineHeight: 1.5,
            }}
            data-testid="policy-template-picker-empty"
          >
            No starter templates are available.
          </div>
        ) : (
          <div className="space-y-4" data-testid="policy-template-picker-list">
            {(templates ?? []).map((t) => {
              const key = keyOf(t);
              const isSelected = selectedKey === key;
              const disabled = !canLoad(t);
              const counts = categoryCountsFor(t.rules);
              return (
                <div
                  key={key}
                  className="p-5"
                  style={{
                    background: isSelected ? C.surface2 : C.surface,
                    border: `1px solid ${isSelected ? C.text : C.border}`,
                    borderRadius: 2,
                  }}
                  data-testid="policy-template-picker-card"
                  data-template-slug={t.template_slug}
                  data-template-version={t.template_version}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div
                        style={{
                          fontFamily: sansFont,
                          fontSize: 15,
                          fontWeight: 600,
                          color: C.text,
                        }}
                      >
                        {t.display_name}
                      </div>
                      <div
                        style={{
                          fontFamily: monoFont,
                          fontSize: 10,
                          color: C.muted,
                          letterSpacing: 1,
                          marginTop: 4,
                        }}
                      >
                        {t.rules.length} RULE{t.rules.length === 1 ? '' : 'S'} INCLUDED
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => onLoad(t)}
                      disabled={disabled}
                      className="flex items-center gap-2 px-4 py-2"
                      style={{
                        background: disabled ? C.surface2 : C.lime,
                        color: disabled ? C.muted : C.ink,
                        fontFamily: sansFont,
                        fontWeight: 600,
                        fontSize: 13,
                        borderRadius: 2,
                        border: `1px solid ${disabled ? C.border : C.lime}`,
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        flexShrink: 0,
                      }}
                      data-testid="policy-template-picker-load-cta"
                    >
                      <Download size={13} /> Load into draft
                    </button>
                  </div>

                  <div className="mt-2.5">
                    <ExpandableText
                      C={C}
                      text={t.description}
                      fontSize={12}
                      color={C.dim}
                    />
                  </div>

                  <div
                    className="mt-5"
                    style={{
                      fontFamily: monoFont,
                      fontSize: 10,
                      color: C.muted,
                      letterSpacing: 1.5,
                      marginBottom: 8,
                    }}
                  >
                    RULE BREAKDOWN
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {counts.map((row) => (
                      <Chip
                        C={C}
                        key={row.id}
                        label={`${row.label.toUpperCase()} · ${row.count}`}
                      />
                    ))}
                  </div>

                  <div
                    className="mt-5"
                    style={{
                      fontFamily: monoFont,
                      fontSize: 10,
                      color: C.muted,
                      letterSpacing: 1.5,
                      marginBottom: 8,
                    }}
                  >
                    RECOMMENDED FOR
                  </div>
                  <ExpandableText
                    C={C}
                    text={t.recommended_for}
                    fontSize={12}
                    color={C.text}
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* Footer — the "loading is one-time, no auto-resync" fact already
            lives in the subtitle above in plain language; a second,
            jargon-heavy restatement here ("ONE-TIME LOAD · NO
            AUTO-RESYNC") added nothing a merchant couldn't already read. */}
        <div
          className="flex items-center justify-end gap-2 pt-3"
          style={{ borderTop: `1px solid ${C.borderSoft}` }}
        >
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2"
            style={{
              background: 'transparent',
              color: C.text,
              border: `1px solid ${C.border}`,
              fontFamily: sansFont,
              fontWeight: 500,
              fontSize: 13,
              borderRadius: 2,
            }}
            data-testid="policy-template-picker-cancel"
          >
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
}
